import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import {
  ApplicationType,
  BulkImportValidateResponse,
  BulkImportResult,
  ImportMode,
  ImportFileType,
  LegacyImportValidateResponse,
  JsonFormatType,
  TranslationImportStatusResponse,
} from '../types/translation-types';
import { TranslationApi } from '../data/translation-api';

// Import step enum for multi-step wizard
export enum ImportStep {
  Upload = 0,
  SelectSheet = 1,      // For multi-sheet Excel
  SelectLanguage = 2,   // For single-language JSON
  Preview = 3,
  Configure = 4,
  Import = 5,
  Complete = 6,
}

interface ImportStore {
  // Current step in the wizard
  currentStep: ImportStep;

  // Selected file
  file: File | null;
  fileName: string | null;
  fileType: ImportFileType;

  // Legacy format detection
  detectedFormat: string | null;
  detectedSheets: string[] | null;
  detectedLanguages: string[] | null;
  detectedJsonFormat: JsonFormatType | null;
  requiresSheetSelection: boolean;
  requiresLanguageSelection: boolean;

  // User selections for legacy imports
  selectedSheet: string | null;
  selectedLanguageCode: string | null;
  targetLanguageCode: string | null;  // Target language for import (null = all languages)

  // Validation result
  validationResult: LegacyImportValidateResponse | null;

  // Import configuration
  selectedAppType: ApplicationType | null;
  importMode: ImportMode;

  // Import result
  importResult: BulkImportResult | null;
  importId: string | null;  // For async import tracking

  // SSE streaming state
  streamAbortController: AbortController | null;
  streamProgress: TranslationImportStatusResponse | null;

  // Loading states
  isValidating: boolean;
  isImporting: boolean;
  isDownloadingTemplate: boolean;
  isExporting: boolean;
  isDetectingFormat: boolean;

  // Error states
  validationError: string | null;
  importError: string | null;

  // Actions
  setCurrentStep: (step: ImportStep) => void;
  setFile: (file: File | null) => void;
  setFileType: (fileType: ImportFileType) => void;
  setSelectedAppType: (appType: ApplicationType | null) => void;
  setImportMode: (mode: ImportMode) => void;
  setSelectedSheet: (sheet: string | null) => void;
  setSelectedLanguageCode: (code: string | null) => void;
  setTargetLanguageCode: (code: string | null) => void;

  detectFileFormat: () => Promise<void>;
  validateFile: () => Promise<void>;
  executeImport: () => Promise<string | null>;  // Returns importId or null on error (legacy polling)
  executeStreamingImport: (onProgress: (progress: TranslationImportStatusResponse) => void) => Promise<TranslationImportStatusResponse | null>;  // SSE streaming import
  cancelStreamingImport: () => void;
  downloadTemplate: () => Promise<void>;
  exportTranslations: () => Promise<void>;

  reset: () => void;
}

const initialState = {
  currentStep: ImportStep.Upload,
  file: null,
  fileName: null,
  fileType: ImportFileType.Excel,
  detectedFormat: null,
  detectedSheets: null,
  detectedLanguages: null,
  detectedJsonFormat: null,
  requiresSheetSelection: false,
  requiresLanguageSelection: false,
  selectedSheet: null,
  selectedLanguageCode: null,
  targetLanguageCode: null,
  validationResult: null,
  selectedAppType: null,
  importId: null,
  importMode: ImportMode.AddNewOnly,
  importResult: null,
  streamAbortController: null,
  streamProgress: null,
  isValidating: false,
  isImporting: false,
  isDownloadingTemplate: false,
  isExporting: false,
  isDetectingFormat: false,
  validationError: null,
  importError: null,
};

export const useImportStore = create<ImportStore>((set, get) => ({
  ...initialState,

  setCurrentStep: (step) => set({ currentStep: step }),

  setFile: (file) => {
    // Preserve the user's target language selection across file uploads
    const currentTargetLanguageCode = get().targetLanguageCode;

    // Auto-detect file type based on extension
    let detectedFileType = get().fileType;
    if (file) {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith('.json')) {
        detectedFileType = ImportFileType.Json;
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        detectedFileType = ImportFileType.Excel;
      }
    }

    set({
      file,
      fileName: file?.name || null,
      fileType: detectedFileType,
      validationResult: null,
      validationError: null,
      importResult: null,
      importError: null,
      detectedFormat: null,
      detectedSheets: null,
      detectedLanguages: null,
      detectedJsonFormat: null,
      requiresSheetSelection: false,
      requiresLanguageSelection: false,
      selectedSheet: null,
      selectedLanguageCode: null,
      targetLanguageCode: currentTargetLanguageCode,
    });
  },

  setFileType: (fileType) => set({
    fileType,
    file: null,
    fileName: null,
    validationResult: null,
    validationError: null,
    detectedFormat: null,
    detectedSheets: null,
    detectedLanguages: null,
    detectedJsonFormat: null,
    requiresSheetSelection: false,
    requiresLanguageSelection: false,
    selectedSheet: null,
    selectedLanguageCode: null,
    targetLanguageCode: null,
  }),

  setSelectedAppType: (appType) => set({
    selectedAppType: appType,
    validationResult: null,
    validationError: null,
  }),

  setImportMode: (mode) => set({ importMode: mode }),

  setSelectedSheet: (sheet) => set({ selectedSheet: sheet }),

  setSelectedLanguageCode: (code) => set({ selectedLanguageCode: code }),

  setTargetLanguageCode: (code) => set({ targetLanguageCode: code }),

  detectFileFormat: async () => {
    const { file, fileType, selectedAppType } = get();

    if (!file) {
      set({ validationError: 'No file selected' });
      return;
    }

    if (!selectedAppType) {
      set({ validationError: 'No application type selected' });
      return;
    }

    set({ isDetectingFormat: true, validationError: null, validationResult: null });

    try {
      const services = servicesContainer.getServices();
      const fileContent = await TranslationApi.fileToBase64(file);

      let result: LegacyImportValidateResponse;

      if (fileType === ImportFileType.Json) {
        // Validate JSON to detect format
        result = await services.translationApi.validateLegacyJsonImport({
          fileContent,
          applicationType: selectedAppType,
          fileName: file.name,
        });
      } else {
        // Validate Excel to detect sheets
        result = await services.translationApi.validateLegacyExcelImport({
          fileContent,
          applicationType: selectedAppType,
        });
      }

      // Determine detected JSON format based on response
      let detectedJsonFormat: JsonFormatType | null = null;
      if (fileType === ImportFileType.Json && result.detectedFormat) {
        if (result.detectedFormat.includes('EvHome')) {
          detectedJsonFormat = JsonFormatType.EvHome;
        } else if (result.detectedFormat.includes('ReactWeb')) {
          detectedJsonFormat = JsonFormatType.ReactWeb;
        }
      }

      set({
        detectedFormat: result.detectedFormat || null,
        detectedSheets: result.detectedSheets || null,
        detectedLanguages: result.detectedLanguages || result.languagesFound || null,
        detectedJsonFormat,
        requiresSheetSelection: result.requiresSheetSelection || false,
        requiresLanguageSelection: result.requiresLanguageSelection || false,
        isDetectingFormat: false,
      });

      // Determine next step based on detection
      if (result.requiresSheetSelection && result.detectedSheets?.length) {
        set({ currentStep: ImportStep.SelectSheet });
      } else if (result.requiresLanguageSelection) {
        set({ currentStep: ImportStep.SelectLanguage });
      } else {
        // Go straight to full validation
        await get().validateFile();
      }
    } catch (error) {
      console.error('ImportStore.detectFileFormat: Failed:', error);
      set({
        validationError: error instanceof Error ? error.message : 'Failed to detect file format',
        isDetectingFormat: false,
      });
    }
  },

  validateFile: async () => {
    const { file, fileType, selectedAppType, selectedSheet, selectedLanguageCode, targetLanguageCode } = get();

    if (!file) {
      set({ validationError: 'No file selected' });
      return;
    }

    if (!selectedAppType) {
      set({ validationError: 'No application type selected' });
      return;
    }

    set({ isValidating: true, validationError: null, validationResult: null });

    try {
      const services = servicesContainer.getServices();
      const fileContent = await TranslationApi.fileToBase64(file);

      let result: LegacyImportValidateResponse;

      if (fileType === ImportFileType.Json) {
        // JSON import validation
        result = await services.translationApi.validateLegacyJsonImport({
          fileContent,
          applicationType: selectedAppType,
          languageCode: selectedLanguageCode || undefined,
          targetLanguageCode: targetLanguageCode || undefined,
          fileName: file?.name,
        });
      } else {
        // Excel import validation
        result = await services.translationApi.validateLegacyExcelImport({
          fileContent,
          applicationType: selectedAppType,
          sheetName: selectedSheet || undefined,
          targetLanguageCode: targetLanguageCode || undefined,
        });
      }

      set({
        validationResult: result,
        detectedFormat: result.detectedFormat || get().detectedFormat,
        detectedLanguages: result.detectedLanguages || result.languagesFound || get().detectedLanguages,
        isValidating: false,
        currentStep: result.isValid ? ImportStep.Preview : ImportStep.Upload,
      });
    } catch (error) {
      console.error('ImportStore.validateFile: Failed:', error);
      set({
        validationError: error instanceof Error ? error.message : 'Failed to validate file',
        isValidating: false,
      });
    }
  },

  executeImport: async () => {
    const { file, fileType, selectedAppType, importMode, selectedSheet, selectedLanguageCode, targetLanguageCode } = get();

    if (!file) {
      set({ importError: 'No file selected' });
      return null;
    }

    set({ isImporting: true, importError: null, importResult: null, importId: null });

    try {
      const services = servicesContainer.getServices();
      const fileContent = await TranslationApi.fileToBase64(file);

      let startResponse;

      if (fileType === ImportFileType.Json) {
        // JSON import - returns immediately with importId
        startResponse = await services.translationApi.legacyJsonImport({
          fileContent,
          applicationType: selectedAppType,
          importMode: importMode,
          languageCode: selectedLanguageCode || undefined,
          fileName: file?.name,
        });
      } else {
        // Excel import - returns immediately with importId
        startResponse = await services.translationApi.legacyExcelImport({
          fileContent,
          applicationType: selectedAppType,
          importMode: importMode,
          sheetName: selectedSheet || undefined,
          targetLanguageCode: targetLanguageCode || undefined,
        });
      }

      // Check if import started successfully
      if (startResponse.isSuccess && startResponse.importId) {
        set({
          importId: startResponse.importId,
          isImporting: false,
        });
        return startResponse.importId;
      } else {
        // Failed to start import
        const errorMsg = startResponse.errorMessage || 'Failed to start import';
        set({
          importError: errorMsg,
          isImporting: false,
        });
        return null;
      }
    } catch (error) {
      console.error('ImportStore.executeImport: Failed:', error);
      set({
        importError: error instanceof Error ? error.message : 'Failed to start import',
        isImporting: false,
      });
      return null;
    }
  },

  executeStreamingImport: async (onProgress) => {
    const { file, fileType, selectedAppType, importMode, selectedSheet, selectedLanguageCode, targetLanguageCode } = get();

    if (!file) {
      set({ importError: 'No file selected' });
      return null;
    }

    // Create abort controller for cancellation
    const abortController = new AbortController();
    set({
      isImporting: true,
      importError: null,
      importResult: null,
      streamAbortController: abortController,
      streamProgress: null,
    });

    try {
      const services = servicesContainer.getServices();
      const fileContent = await TranslationApi.fileToBase64(file);

      let finalResult: TranslationImportStatusResponse;

      if (fileType === ImportFileType.Json) {
        // Use SSE streaming endpoint for JSON
        finalResult = await services.translationApi.legacyJsonImportStream(
          {
            fileContent,
            applicationType: selectedAppType!,
            importMode: importMode,
            languageCode: selectedLanguageCode || undefined,
            fileName: file.name,
          },
          (progress, eventType) => {
            // Update store with progress
            set({ streamProgress: progress });
            // Call the callback for UI updates
            onProgress(progress);
            console.log('ImportStore.executeStreamingImport: JSON Progress event:', eventType, progress.percentComplete, '%');
          },
          abortController.signal
        );
      } else {
        // Use SSE streaming endpoint for Excel
        finalResult = await services.translationApi.legacyExcelImportStream(
          {
            fileContent,
            applicationType: selectedAppType!,
            importMode: importMode,
            sheetName: selectedSheet || undefined,
            targetLanguageCode: targetLanguageCode || undefined,
          },
          (progress, eventType) => {
            // Update store with progress
            set({ streamProgress: progress });
            // Call the callback for UI updates
            onProgress(progress);
            console.log('ImportStore.executeStreamingImport: Excel Progress event:', eventType, progress.percentComplete, '%');
          },
          abortController.signal
        );
      }

      set({
        isImporting: false,
        streamAbortController: null,
        streamProgress: finalResult,
      });

      return finalResult;
    } catch (error) {
      // Check if it was cancelled
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('ImportStore.executeStreamingImport: Import cancelled');
        set({
          importError: 'Import cancelled',
          isImporting: false,
          streamAbortController: null,
        });
        return null;
      }

      console.error('ImportStore.executeStreamingImport: Failed:', error);
      set({
        importError: error instanceof Error ? error.message : 'Failed to import',
        isImporting: false,
        streamAbortController: null,
      });
      return null;
    }
  },

  cancelStreamingImport: () => {
    const { streamAbortController } = get();
    if (streamAbortController) {
      streamAbortController.abort();
      set({
        streamAbortController: null,
        isImporting: false,
      });
    }
  },

  downloadTemplate: async () => {
    const { selectedAppType } = get();

    set({ isDownloadingTemplate: true });

    try {
      const services = servicesContainer.getServices();
      const result = await services.downloadTemplateUseCase.execute(selectedAppType);

      if (!result.isSuccess) {
        throw new Error(result.errorMessage || 'Download failed');
      }

      // Decode base64 and create blob
      const byteCharacters = atob(result.fileContent);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: result.contentType });

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      set({ isDownloadingTemplate: false });
    } catch (error) {
      console.error('ImportStore.downloadTemplate: Failed:', error);
      set({ isDownloadingTemplate: false });
      throw error;
    }
  },

  exportTranslations: async () => {
    const { selectedAppType } = get();

    set({ isExporting: true });

    try {
      const services = servicesContainer.getServices();
      const result = await services.exportTranslationsUseCase.execute(selectedAppType);

      if (!result.isSuccess) {
        throw new Error(result.errorMessage || 'Export failed');
      }

      // Decode base64 and create blob
      const byteCharacters = atob(result.fileContent);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: result.contentType });

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      set({ isExporting: false });
    } catch (error) {
      console.error('ImportStore.exportTranslations: Failed:', error);
      set({ isExporting: false });
      throw error;
    }
  },

  reset: () => {
    // Cancel any streaming import in progress
    const { streamAbortController, selectedAppType } = get();
    if (streamAbortController) {
      streamAbortController.abort();
    }
    // Preserve selectedAppType when resetting (it comes from shared translation store)
    set({ ...initialState, selectedAppType });
  },
}));
